import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UserPlus, Copy, Check, X, Trash2, RefreshCw, Shield, Mail } from "lucide-react";
import { api } from "@/platform/api";
import { useAuth } from "@/platform/AuthContext";
import { Button } from "@/platform/components/primitives";

const ROLE_BADGE = {
  owner: { label: "Owner", cls: "bg-[#7C6BFF22] text-[#A99BFF]" },
  admin: { label: "Admin", cls: "bg-[#4F9CFF22] text-[#7FB6FF]" },
  member: { label: "Member", cls: "bg-[#2A3040] text-[#AAB1BF]" },
};

function Avatar({ name, url, size = 34 }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const hue = [...(name || "x")].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return url ? (
    <img src={url} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />
  ) : (
    <span className="grid shrink-0 place-items-center rounded-full text-[13px] font-semibold text-white" style={{ width: size, height: size, background: `hsl(${hue} 45% 45%)` }}>{initial}</span>
  );
}

function CopyField({ value, testid }) {
  const [done, setDone] = useState(false);
  return (
    <div className="flex items-center gap-2 rounded-lg border border-[#2A3040] bg-[#0E1016] px-3 py-2">
      <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-[#98A0B0]">{value}</span>
      <button onClick={() => { navigator.clipboard?.writeText(value); setDone(true); setTimeout(() => setDone(false), 1600); }}
        className="inline-flex items-center gap-1 text-[12px] font-medium text-[#A99BFF]" data-testid={testid}>
        {done ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
      </button>
    </div>
  );
}

function InviteModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState(null);

  const create = async () => {
    setBusy(true); setError("");
    try {
      const res = await api.post("/api/workspace/invites", { email: email.trim(), role });
      setLink(`${window.location.origin}/join/${res.token}`);
      onCreated?.();
    } catch (e) { setError(e.message || "Couldn't create the invitation."); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} data-testid="invite-team-modal">
      <div className="w-full max-w-md rounded-2xl border border-[#242A38] bg-[#151A24] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[17px] font-semibold text-[#F4F5F8]">Invite team member</h2>
          <button onClick={onClose} className="text-[#6E7686] hover:text-[#F4F5F8]"><X className="h-5 w-5" /></button>
        </div>
        {link ? (
          <div data-testid="invite-ready">
            <div className="mb-2 flex items-center gap-2 text-[14px] font-medium text-[#4ADE80]"><Check className="h-4 w-4" /> Invitation ready</div>
            <p className="mb-3 text-[13px] text-[#98A0B0]">Send this link to <span className="text-[#E6E8EE]">{email}</span>. It expires in 7 days.</p>
            <CopyField value={link} testid="invite-copy-link" />
            <div className="mt-5 flex justify-end"><Button variant="outline" onClick={onClose} className="sm:w-auto sm:px-5">Done</Button></div>
          </div>
        ) : (
          <>
            <label className="mb-1.5 block text-[12.5px] font-medium text-[#AAB1BF]">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="designer@agency.com"
              className="mb-4 w-full rounded-lg border border-[#2A3040] bg-[#0E1016] px-3 py-2.5 text-[14px] text-[#F4F5F8] outline-none focus:border-[#6D5EF6]" data-testid="invite-email-input" />
            <label className="mb-1.5 block text-[12.5px] font-medium text-[#AAB1BF]">Workspace role</label>
            <div className="mb-5 grid grid-cols-2 gap-2">
              {["admin", "member"].map((r) => (
                <button key={r} onClick={() => setRole(r)} className={`rounded-lg border px-3 py-2.5 text-left text-[13.5px] transition-colors ${role === r ? "border-[#6D5EF6] bg-[#6D5EF612] text-[#F4F5F8]" : "border-[#2A3040] text-[#98A0B0]"}`} data-testid={`invite-role-${r}`}>
                  <span className="font-medium capitalize">{r}</span>
                  <span className="mt-0.5 block text-[11.5px] text-[#6E7686]">{r === "admin" ? "All projects · manage team" : "Assigned projects only"}</span>
                </button>
              ))}
            </div>
            {error && <p className="mb-3 text-[13px] text-[#F87171]" data-testid="invite-error">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} className="sm:w-auto sm:px-5">Cancel</Button>
              <Button onClick={create} loading={busy} disabled={!email.trim()} className="sm:w-auto sm:px-5" data-testid="invite-create-btn">Create invitation</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InviteCopyBtn({ url, id }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <button onClick={copy} className="inline-flex items-center gap-1 text-[12px] font-medium text-[#A99BFF] hover:underline" data-testid={`invite-copy-${id}`}>
      {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy link</>}
    </button>
  );
}

export default function Team() {
  const { user, workspace } = useAuth();
  const role = workspace?.role;
  const canManage = role === "owner" || role === "admin";
  const [members, setMembers] = useState(null);
  const [invites, setInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [regenLink, setRegenLink] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = useCallback(async () => {
    try {
      const m = await api.get("/api/workspace/members");
      setMembers(m.members || []);
      if (canManage) { const i = await api.get("/api/workspace/invites"); setInvites(i.invites || []); }
    } catch (_e) { setMembers([]); }
  }, [canManage]);
  useEffect(() => { load(); }, [load]);

  const changeRole = async (uid, newRole) => {
    try { await api.patch(`/api/workspace/members/${uid}/role`, { role: newRole }); load(); } catch (e) { alert(e.message); }
  };
  const removeMember = async (uid) => {
    try { await api.del(`/api/workspace/members/${uid}`); setConfirmRemove(null); load(); } catch (e) { alert(e.message); }
  };
  const revokeInvite = async (id) => { try { await api.post(`/api/workspace/invites/${id}/revoke`); load(); } catch (e) { alert(e.message); } };
  const regenInvite = async (id, email) => {
    try { const r = await api.post(`/api/workspace/invites/${id}/regenerate`); setRegenLink({ email, url: `${window.location.origin}/join/${r.token}` }); load(); } catch (e) { alert(e.message); }
  };

  const onlyOwner = useMemo(() => (members || []).filter((m) => m.role === "owner").length <= 1, [members]);

  return (
    <div className="mx-auto max-w-4xl animate-in fade-in duration-300" data-testid="team-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight text-[#F4F5F8]">Team</h1>
          <p className="mt-0.5 text-[14px] text-[#98A0B0]">Manage the people working in your Pinlayr workspace.</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowInvite(true)} className="sm:w-auto sm:px-5" data-testid="invite-team-btn"><UserPlus className="h-4 w-4" /> Invite team member</Button>
        )}
      </div>

      {/* Members */}
      <p className="mb-2.5 mt-7 text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Members {members ? `· ${members.length}` : ""}</p>
      {members === null ? (
        <div className="grid place-items-center py-16"><span className="h-6 w-6 animate-spin rounded-full border-2 border-[#6D5EF6] border-t-transparent" /></div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#242A38]" data-testid="team-members">
          {members.map((m) => {
            const badge = ROLE_BADGE[m.role] || ROLE_BADGE.member;
            const editable = canManage && m.role !== "owner" && m.userId !== user?.id;
            return (
              <div key={m.userId} className="flex flex-wrap items-center gap-3 border-b border-[#1C2130] bg-[#12151D] px-4 py-3.5 last:border-b-0" data-testid={`team-member-${m.userId}`}>
                <Avatar name={m.fullName} url={m.avatarUrl} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-medium text-[#E6E8EE]">{m.fullName}{m.userId === user?.id && <span className="ml-1.5 text-[11.5px] text-[#6E7686]">(you)</span>}</p>
                  <p className="truncate text-[12.5px] text-[#6E7686]">{m.email}</p>
                </div>
                {m.role === "member" && <span className="hidden text-[12px] text-[#7B8496] sm:inline">{m.projectCount} project{m.projectCount === 1 ? "" : "s"}</span>}
                {editable ? (
                  <select value={m.role} onChange={(e) => changeRole(m.userId, e.target.value)} className="rounded-lg border border-[#2A3040] bg-[#0E1016] px-2 py-1.5 text-[12.5px] text-[#E6E8EE]" data-testid={`team-role-${m.userId}`}>
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                  </select>
                ) : (
                  <span className={`rounded-full px-2.5 py-1 text-[11.5px] font-semibold ${badge.cls}`}>{badge.label}</span>
                )}
                {editable && (
                  <button onClick={() => setConfirmRemove(m)} className="text-[#6E7686] hover:text-[#F87171]" data-testid={`team-remove-${m.userId}`}><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {members && members.length <= 1 && invites.length === 0 && (
        <div className="mt-3 rounded-2xl border border-dashed border-[#2A3040] bg-[#12151D] p-8 text-center" data-testid="team-empty">
          <p className="text-[15px] font-semibold text-[#F4F5F8]">Build your team</p>
          <p className="mt-1 text-[13.5px] text-[#98A0B0]">Invite teammates to collaborate on Pinlayr projects.</p>
          {canManage && <div className="mt-4 flex justify-center"><Button onClick={() => setShowInvite(true)} className="sm:w-auto sm:px-5"><UserPlus className="h-4 w-4" /> Invite team member</Button></div>}
        </div>
      )}

      {/* Pending invites */}
      {canManage && invites.length > 0 && (
        <>
          <p className="mb-2.5 mt-8 text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Pending invites · {invites.length}</p>
          <div className="overflow-hidden rounded-2xl border border-[#242A38]" data-testid="team-invites">
            {invites.map((inv) => (
              <div key={inv.id} className="flex flex-wrap items-center gap-3 border-b border-[#1C2130] bg-[#12151D] px-4 py-3.5 last:border-b-0" data-testid={`team-invite-${inv.id}`}>
                <span className="grid h-9 w-9 place-items-center rounded-full bg-[#1C2130]"><Mail className="h-4 w-4 text-[#6E7686]" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] text-[#E6E8EE]">{inv.email}</p>
                  <p className="text-[12px] text-[#6E7686]"><span className="capitalize">{inv.role}</span> · {inv.status === "expired" ? "Expired" : "Pending"}</p>
                </div>
                {inv.token && inv.status === "pending" && <InviteCopyBtn url={`${window.location.origin}/join/${inv.token}`} id={inv.id} />}
                <button onClick={() => regenInvite(inv.id, inv.email)} className="inline-flex items-center gap-1 text-[12px] font-medium text-[#A99BFF] hover:underline" data-testid={`invite-regen-${inv.id}`}><RefreshCw className="h-3.5 w-3.5" /> Regenerate link</button>
                <button onClick={() => revokeInvite(inv.id)} className="text-[12px] font-medium text-[#6E7686] hover:text-[#F87171]" data-testid={`invite-revoke-${inv.id}`}>Revoke</button>
              </div>
            ))}
          </div>
        </>
      )}

      {regenLink && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setRegenLink(null)}>
          <div className="w-full max-w-md rounded-2xl border border-[#242A38] bg-[#151A24] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center gap-2 text-[14px] font-medium text-[#4ADE80]"><Check className="h-4 w-4" /> New link ready</div>
            <p className="mb-3 text-[13px] text-[#98A0B0]">Send this new link to <span className="text-[#E6E8EE]">{regenLink.email}</span>. The old link no longer works.</p>
            <CopyField value={regenLink.url} testid="regen-copy-link" />
            <div className="mt-5 flex justify-end"><Button variant="outline" onClick={() => setRegenLink(null)} className="sm:w-auto sm:px-5">Done</Button></div>
          </div>
        </div>
      )}

      {confirmRemove && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setConfirmRemove(null)}>
          <div className="w-full max-w-md rounded-2xl border border-[#242A38] bg-[#151A24] p-6" onClick={(e) => e.stopPropagation()} data-testid="remove-member-modal">
            <h2 className="text-[17px] font-semibold text-[#F4F5F8]">Remove {confirmRemove.fullName} from this workspace?</h2>
            <p className="mt-2 text-[13.5px] text-[#98A0B0]">{confirmRemove.fullName} will lose access to Pinlayr projects in this workspace. Their existing comments and activity will remain.</p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmRemove(null)} className="sm:w-auto sm:px-5">Cancel</Button>
              <Button onClick={() => removeMember(confirmRemove.userId)} className="sm:w-auto sm:px-5 !bg-[#DC2626]" data-testid="confirm-remove-btn">Remove</Button>
            </div>
          </div>
        </div>
      )}

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onCreated={load} />}
    </div>
  );
}
